import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  TablePagination,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import apiClient from '../../api/client';
import { theme } from '../../styles/theme';

interface CollectionResult {
  id: number;
  request_id: string;
  metric_id: number;
  country_id: number;
  year: number;
  status: string;
  search_queries: string[];
  sources_found: any;
  independent_source_count: number;
  extracted_value?: number;
  processing_duration_ms?: number;
  error_message?: string;
  confidence_level?: string;
  created_at: string;
  updated_at: string;
  // Metadata from joined request
  country_code?: string;
  country_name?: string;
  metric_name?: string;
  metric_description?: string;
}

const CollectionResults: React.FC = () => {
  const [results, setResults] = useState<CollectionResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchResults();
  }, [page, rowsPerPage]);

  const fetchResults = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/collection/results', {
        params: {
          limit: rowsPerPage,
          offset: page * rowsPerPage,
        },
      });
      // apiClient.get already unwraps the data field
      setResults(response.results || []);
      setTotalCount(response.total_count || 0);
    } catch (error) {
      console.error('Failed to fetch collection results:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'success':
        return <CheckCircleIcon sx={{ color: theme.colors.status.success }} fontSize="small" />;
      case 'failed':
        return <ErrorIcon sx={{ color: theme.colors.status.error }} fontSize="small" />;
      case 'partial':
        return <WarningIcon sx={{ color: theme.colors.status.warning }} fontSize="small" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status.toLowerCase()) {
      case 'success':
        return 'success';
      case 'failed':
        return 'error';
      case 'partial':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getConfidenceColor = (level?: string): string => {
    switch (level?.toLowerCase()) {
      case 'high':
        return theme.colors.status.success;
      case 'medium':
        return theme.colors.status.warning;
      case 'low':
        return theme.colors.status.error;
      default:
        return theme.colors.text.secondary;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const filteredResults = results.filter(result => {
    const searchLower = searchTerm.toLowerCase();
    return (
      result.country_name?.toLowerCase().includes(searchLower) ||
      result.country_code?.toLowerCase().includes(searchLower) ||
      result.metric_name?.toLowerCase().includes(searchLower) ||
      result.request_id.toLowerCase().includes(searchLower) ||
      result.extracted_value?.toString().includes(searchTerm)
    );
  });

  if (loading && results.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress sx={{ color: theme.colors.primary.main }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontFamily: theme.fonts.primary,
            color: theme.colors.text.primary,
            fontWeight: 400
          }}
        >
          Collection Results
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.colors.text.secondary,
            fontFamily: theme.fonts.primary
          }}
        >
          View completed data collection results and extracted values
        </Typography>
      </Box>

      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search by country, metric, request ID, or value..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            fontFamily: theme.fonts.primary,
            '& fieldset': {
              borderColor: theme.colors.border.light,
            },
            '&:hover fieldset': {
              borderColor: theme.colors.primary.main,
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.colors.primary.main,
            },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: theme.colors.text.secondary }} />
            </InputAdornment>
          ),
        }}
      />

      <TableContainer component={Paper} sx={{
        borderRadius: theme.widget.borderRadius,
        boxShadow: theme.widget.boxShadow,
        '&:hover': {
          boxShadow: theme.widget.hoverBoxShadow,
        },
      }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Request ID</TableCell>
              <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Country</TableCell>
              <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Metric</TableCell>
              <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Year</TableCell>
              <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Value</TableCell>
              <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Confidence</TableCell>
              <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Sources</TableCell>
              <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Status</TableCell>
              <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Duration</TableCell>
              <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Completed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredResults.map((result) => (
              <TableRow key={result.id}>
                <TableCell>
                  <Tooltip title={result.request_id}>
                    <Typography variant="caption" sx={{ fontFamily: theme.fonts.mono }}>
                      {result.request_id.substring(0, 12)}...
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontFamily: theme.fonts.primary }}>
                      {result.country_name || result.country_code || `Country ${result.country_id}`}
                    </Typography>
                    {result.country_code && result.country_name && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.colors.text.secondary,
                          fontFamily: theme.fonts.primary
                        }}
                      >
                        {result.country_code}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Tooltip title={result.metric_description || ''}>
                    <Typography variant="body2" sx={{ fontFamily: theme.fonts.primary }}>
                      {result.metric_name || `Metric ${result.metric_id}`}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ fontFamily: theme.fonts.primary }}>{result.year}</TableCell>
                <TableCell>
                  {result.extracted_value !== undefined && result.extracted_value !== null ? (
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: theme.fonts.mono,
                        fontWeight: 500,
                        color: theme.colors.primary.main
                      }}
                    >
                      {result.extracted_value.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.colors.text.secondary,
                        fontFamily: theme.fonts.primary
                      }}
                    >
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {result.confidence_level && (
                    <Chip
                      label={result.confidence_level}
                      size="small"
                      sx={{
                        backgroundColor: `${getConfidenceColor(result.confidence_level)}20`,
                        color: getConfidenceColor(result.confidence_level),
                        border: `1px solid ${getConfidenceColor(result.confidence_level)}`,
                      }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: theme.fonts.primary }}>
                    {result.independent_source_count || 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {getStatusIcon(result.status)}
                    <Chip
                      label={result.status}
                      color={getStatusColor(result.status)}
                      size="small"
                    />
                  </Box>
                  {result.error_message && (
                    <Tooltip title={result.error_message}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 0.5,
                          color: theme.colors.status.error,
                          fontFamily: theme.fonts.primary
                        }}
                      >
                        Error: {result.error_message.substring(0, 30)}...
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: theme.fonts.primary }}>
                    {formatDuration(result.processing_duration_ms)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: theme.fonts.primary }}>
                    {formatDate(result.created_at)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {filteredResults.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography
                    sx={{
                      py: 3,
                      color: theme.colors.text.secondary,
                      fontFamily: theme.fonts.primary
                    }}
                  >
                    {searchTerm
                      ? 'No results match your search'
                      : 'No collection results found'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>
    </Box>
  );
};

export default CollectionResults;